import CryptoJS from 'crypto-js';

/**
 * Déchiffre la charge utile d'un QR code de provisionnement émis par le
 * backend TicketNGo (TicketNGo.Ticketing.Services.EncryptionService).
 *
 * Format nouveau (préféré) : base64( IV(16 octets) || chiffré ) en AES-256-CBC.
 * L'IV est aléatoire par opération côté serveur, ce qui empêche l'analyse
 * de motifs sur des QR successifs émis pour la même clé API.
 *
 * Format legacy : base64( chiffré ) avec IV fixe — supporté en
 * rétrocompatibilité durant la fenêtre de déploiement, à retirer une fois
 * tous les serveurs migrés.
 *
 * SÉCURITÉ : la clé maître est embarquée dans le bundle mobile via
 * REACT_APP_ENCRYPTION_KEY (build-time). Cela offre une obfuscation
 * raisonnable contre l'observation casuelle du QR, mais un attaquant qui
 * décompile l'app peut récupérer la clé. La vraie crédential reste l'ApiKey
 * (Guid) sous-jacent. Ne jamais committer la valeur de la clé dans le
 * dépôt — la définir uniquement dans un fichier .env local (gitignored)
 * ou via la CI au moment du build.
 */

const KEY_RAW = process.env.REACT_APP_ENCRYPTION_KEY || '';

// Helpers de parsing de la clé (base64 préféré, hex toléré, UTF-8 fallback).
function parseKey(raw) {
    const trimmed = (raw || '').trim();
    if (!trimmed) {
        throw new Error(
            "REACT_APP_ENCRYPTION_KEY non défini. Configurer la clé dans .env (32 octets, base64 préféré)."
        );
    }
    // base64 → 32 octets ?
    try {
        const decoded = CryptoJS.enc.Base64.parse(trimmed);
        if (decoded.sigBytes === 32) return decoded;
    } catch (_) { /* ignorer */ }
    // hex 64 chars ?
    if (/^[0-9a-fA-F]{64}$/.test(trimmed)) {
        return CryptoJS.enc.Hex.parse(trimmed);
    }
    // UTF-8 brut (32 chars exact) — toléré pour compat dev
    if (trimmed.length === 32) {
        return CryptoJS.enc.Utf8.parse(trimmed);
    }
    throw new Error(
        `REACT_APP_ENCRYPTION_KEY doit décoder à 32 octets (base64 préféré). Reçu : ${trimmed.length} caractères bruts.`
    );
}

const KEY = parseKey(KEY_RAW);

// Format legacy (IV fixe) — à retirer après migration complète.
const LEGACY_IV = CryptoJS.enc.Utf8.parse('abcdef9876543210');

export function decryptData(encryptedData) {
    if (!encryptedData) {
        throw new Error('Charge utile chiffrée vide.');
    }

    // Tentative format nouveau : IV(16) prependé au chiffré.
    try {
        const raw = CryptoJS.enc.Base64.parse(encryptedData);
        if (raw.sigBytes >= 32) {
            const ivWords = CryptoJS.lib.WordArray.create(raw.words.slice(0, 4), 16);
            const ctWords = CryptoJS.lib.WordArray.create(
                raw.words.slice(4),
                raw.sigBytes - 16
            );
            const decrypted = CryptoJS.AES.decrypt(
                { ciphertext: ctWords },
                KEY,
                {
                    keySize: 256 / 32,
                    iv: ivWords,
                    mode: CryptoJS.mode.CBC,
                    padding: CryptoJS.pad.Pkcs7,
                }
            );
            const text = decrypted.toString(CryptoJS.enc.Utf8);
            if (text && text.length > 0) {
                return text;
            }
        }
    } catch (_) {
        // Fallback ci-dessous.
    }

    // Fallback : format legacy avec IV fixe (à retirer après migration).
    const decryptedLegacy = CryptoJS.AES.decrypt(encryptedData, KEY, {
        keySize: 256 / 32,
        iv: LEGACY_IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
    });
    return decryptedLegacy.toString(CryptoJS.enc.Utf8);
}
